/**
 * Dynamic Yard Map Page.
 *
 * Thin page wrapper that renders a yard selector and delegates to
 * YardMapView for the interactive canvas. Used by the Inventory Hub
 * as the "Yard Map" tab.
 */

import { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import YardMapView, {
  YardMapErrorBoundary,
  YardUtilizationBar,
} from "@/features/yards/YardMapView";
import type { YardListItem } from "@/types/yard";

function YardMapPage() {
  const [activeYardId, setActiveYardId] = useState<string | null>(null);

  const { data: yards, isLoading: yardsLoading } = useQuery<YardListItem[]>({
    queryKey: ["yards"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/yards")).data as YardListItem[],
  });

  // Auto-select the first yard if none selected.
  useEffect(() => {
    if (!activeYardId && yards && yards.length > 0) {
      setActiveYardId(yards[0]!.id);
    }
  }, [activeYardId, yards]);

  if (yardsLoading) return <LoadingSpinner className="py-12" />;

  if (!yards || yards.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center py-12">
          <div className="empty-state-icon">
            <MapPin />
          </div>
          <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
            No facility maps configured
          </h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Create facility maps in Settings → Facilities to start mapping asset
            locations.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Facility map selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            Facility Map
          </h3>
          <select
            value={activeYardId || ""}
            onChange={(e) => setActiveYardId(e.target.value)}
            className="h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)]"
          >
            {yards.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name} ({y.section_count} sections · {y.total_assets} assets)
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Utilization metrics */}
      {activeYardId && <YardUtilizationBar yardId={activeYardId} />}

      {/* Yard map view */}
      {activeYardId && (
        <YardMapErrorBoundary onReset={() => setActiveYardId(null)}>
          <YardMapView yardId={activeYardId} />
        </YardMapErrorBoundary>
      )}
    </div>
  );
}

export default YardMapPage;
